import {
  type Account,
  type DetailedAccount,
  type Institution,
  type Member,
  AccountType,
  ConnectionStatus,
  ErrorStatuses,
  MfaStatuses,
  ProcessingStatuses,
} from '../'

export const isAccountLiability = (account: Account) =>
  account &&
  (account.account_type === AccountType.CREDIT_CARD ||
    account.account_type === AccountType.LOAN ||
    account.account_type === AccountType.MORTGAGE ||
    account.account_type === AccountType.LINE_OF_CREDIT ||
    account.account_type === AccountType.CHECKING_LINE_OF_CREDIT)

/**
 * Returns the balance to use as an account's spendable / starting balance.
 * For checking/savings accounts it uses available_balance when present and
 * enabled via config, otherwise the current balance. All other account types
 * use the current balance. Single source of truth shared by the Accounts,
 * Financial Strength, and Cash Flow experiences.
 */
export const getAccountStartingBalance = (
  account: Account,
  showAvailableBalance: boolean,
): number => {
  const isCheckingOrSavings =
    account.account_type === AccountType.CHECKING || account.account_type === AccountType.SAVINGS

  if (isCheckingOrSavings && account.available_balance != null && showAvailableBalance) {
    return account.available_balance
  }

  return account.balance || 0
}

export const isManual = (account: Account) => account.is_manual || !account.guid

export const usesCreditLimitField = (account: Account) =>
  account.account_type === AccountType.LINE_OF_CREDIT ||
  account.account_type === AccountType.CREDIT_CARD ||
  account.account_type === AccountType.CHECKING_LINE_OF_CREDIT

export const usesInterestRateField = (account: Account) =>
  account.account_type === AccountType.SAVINGS ||
  account.account_type === AccountType.CHECKING ||
  account.account_type === AccountType.LINE_OF_CREDIT ||
  account.account_type === AccountType.CREDIT_CARD ||
  account.account_type === AccountType.LOAN ||
  account.account_type === AccountType.MORTGAGE

export const usesOriginalBalanceField = (account: Account) =>
  account.account_type === AccountType.LOAN || account.account_type === AccountType.MORTGAGE

export const augmentAccounts = (
  accounts: Account[],
  institutions: Institution[],
  members: Member[],
  shouldExcludeExternalAccounts: boolean,
  shouldExcludeHeldAccounts: boolean,
): DetailedAccount[] => {
  return accounts
    .filter((account) => !(account.member_is_managed_by_user && shouldExcludeExternalAccounts))
    .filter((account) => !(!account.member_is_managed_by_user && shouldExcludeHeldAccounts))
    .sort(accountSorter)
    .map((account) => {
      const institution = institutions.find((inst) => inst.guid === account.institution_guid)
      const member = members.find((member) => member.guid === account.member_guid)

      return buildDetailedAccount(account, institution, member)
    })
}

const buildDetailedAccount = (account: Account, institution?: Institution, member?: Member) => {
  // TODO: Needs to be localized here
  let connectionStatus = ConnectionStatus.Connected
  if (member) {
    if (ErrorStatuses.includes(member.connection_status)) {
      connectionStatus = ConnectionStatus.Error
    } else if (ProcessingStatuses.includes(member.connection_status)) {
      connectionStatus = ConnectionStatus.Processing
    } else if (MfaStatuses.includes(member.connection_status)) {
      connectionStatus = ConnectionStatus.RequiresMfa
    }
  }

  const displayName = [
    account.user_name || account.feed_name || '',
    account.account_number ? `*${account.account_number}` : '',
  ]
    .join(' ')
    .trim()

  return {
    ...account,
    connectionStatus,
    displayName,
    institution,
    member,
  }
}

const accountSorter = (a: Account, b: Account) => {
  // orders by account type descending (Checking -> Savings -> Credit card)
  if (a.account_type > b.account_type) return 1
  if (a.account_type < b.account_type) return -1
  // orders by name (Emergency Savings -> Health Savings -> Rainy Day Savings)
  if ((a.name || '') > (b.name || '')) return 1
  if ((a.name || '') < (b.name || '')) return -1
  return 0
}
